import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { FeatureFlagDxpService } from '@pure1/data';
import { map, Observable } from 'rxjs';

import { stripLeadingOrgUid } from '../../data/utils/org-url';
import { NON_ORG_SCOPED_ROUTES, ORG_URL_FF } from './org-url-shared';

/**
 * canMatch on the `/orgs/:orgUid` parent route.
 *
 * FF off: strips the `/orgs/<uid>` prefix and redirects to the bare path.
 * FF on: non-org-scoped routes (NON_ORG_SCOPED_ROUTES) are redirected to their bare
 * path; all other routes match so `orgUidValidatorGuard` (canActivate) can validate
 * the uid.
 *
 * NOTE: Angular passes ALL URL segments to canMatch, not just child segments.
 * For `/orgs/abc/fleet`, segments = [orgs, abc, fleet]. First child is at index 2.
 */
export const orgScopedRouteMatchGuard: CanMatchFn = (
    _route: Route,
    segments: UrlSegment[],
): Observable<boolean | UrlTree> => {
    const featureFlagService = inject(FeatureFlagDxpService);
    const router = inject(Router);
    return featureFlagService.getFeatureFlag(ORG_URL_FF).pipe(
        map(status => {
            if (!status?.enabled) {
                return router.parseUrl(stripLeadingOrgUid(router.url));
            }
            const firstChildPath = segments[2]?.path;
            if (firstChildPath && NON_ORG_SCOPED_ROUTES.has(firstChildPath)) {
                const childPath = segments
                    .slice(2)
                    .map(s => s.path)
                    .join('/');
                return router.parseUrl(childPath ? '/' + childPath : '/');
            }
            return true;
        }),
    );
};
